import { PageController } from '@client/components/_core/layout/PageController';
import { Loading } from '@client/components/_ui/feedback/Loading';
// Static on purpose: autoCodeSplitting already route-scopes this component, so a nested lazy() only adds a second
// network hop after the route chunk lands (measured 227–1,230 ms of dead time in production traces).
import { IncidentForm } from '@client/components/incidents/incident-form/IncidentForm';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Card } from 'antd';
import type { PropsWithChildren } from 'react';
import { z } from 'zod';

export const Route = createFileRoute('/console/$orgId/compliance/incidents/add')({
    staticData: {
        metadata: {
            title: 'New incident',
        },
        ui: {
            widthVariant: 'compact',
            // Wider than the compact default (w-4xl) so the description
            // textarea and the paired timeline fields get breathing room.
            className: 'w-6xl max-w-full',
        },
    },
    validateSearch: zodValidator(
        z.object({
            messageClassificationId: z.coerce.bigint().optional().catch(undefined),
            // Set when logging an incident from a compliance audit finding
            // (CUR-168 / REQ-FND-7) — links the new incident back to the finding.
            complianceAuditFindingId: z.coerce.bigint().optional().catch(undefined),
            complaint: z
                .object({
                    complaintId: z.coerce.bigint().optional().catch(undefined),
                    handlingPartyIds: z.array(z.coerce.bigint()).optional().catch(undefined),
                    ownerId: z.coerce.bigint().optional().catch(undefined),
                    products: z.array(z.coerce.number()).optional().catch(undefined),
                    policyNo: z.string().optional().catch(undefined),
                    externalClaimId: z.string().optional().catch(undefined),
                })
                .strict()
                .optional()
                .catch(undefined),
        }),
    ),
    pendingMs: 0,
    pendingMinMs: 0,
    pendingComponent: PendingComponent,
    component: RouteComponent,
});

function PendingComponent() {
    return (
        <IncidentFormShell>
            <Loading className="min-h-[360px] py-12" />
        </IncidentFormShell>
    );
}

function IncidentFormShell({ children }: PropsWithChildren) {
    return (
        <PageController route={Route}>
            <Card>
                <div className="pb-6">
                    <h2 className="m-0 font-semibold text-foreground text-xl">New incident</h2>
                </div>
                {children}
            </Card>
        </PageController>
    );
}

function RouteComponent() {
    const orgId = useOrgId();
    const { messageClassificationId, complianceAuditFindingId, complaint } = Route.useSearch();
    const navigate = useNavigate();

    return (
        <IncidentFormShell>
            <IncidentForm
                messageClassificationId={messageClassificationId}
                complianceAuditFindingId={complianceAuditFindingId}
                complaintData={complaint}
                onCancel={() => navigate({ to: '/console/$orgId/compliance/incidents', params: { orgId } })}
                onAfterSaveAccessDenied={() =>
                    navigate({ to: '/console/$orgId/compliance/incidents', params: { orgId } })
                }
                onAfterSaveCancel={({ id }) =>
                    navigate({
                        to: '/console/$orgId/compliance/incidents/details/$id/manage',
                        params: { orgId, id },
                    })
                }
            />
        </IncidentFormShell>
    );
}
